import { z } from 'zod';
import { IdSchema, IdempotencyKeySchema, IsoDateTimeSchema, withMeta } from '../common';
import { ConvGroupSchema } from '../dataset/core';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** voice.* — kernel router for the voice layer and the ⌘K composer (SDD §20; §7.4 Voice surfaces row: voice.ticket /
 *  sessionState + orchestrator WS). No skill ever holds an order tool (ADR-006): nothing here can move money or risk. */

export const VoiceStateSchema = z.enum(['idle', 'listening', 'thinking', 'speaking']);
export type VoiceState = z.infer<typeof VoiceStateSchema>;
export const VoiceLaneSchema = z.enum(['realtime', 'pipeline', 'text']);
export const VoiceSkillSchema = z.enum(['metrics', 'training', 'coaching', 'journal', 'workflow']);

export const VoiceTicketSchema = withMeta({
  /** 60s device-bound ticket — never a provider credential. */
  ticket: z.string(),
  expiresAt: IsoDateTimeSchema,
  relayUrl: z.string(),
  lane: VoiceLaneSchema,
});
export const VoiceSessionStateSchema = withMeta({
  sessionId: IdSchema.nullable(),
  state: VoiceStateSchema,
  lane: VoiceLaneSchema,
  /** Barge-in cue: speaking → listening with context retained. */
  bargeInAvailable: z.boolean(),
  /** Minutes exhaustion degrades to text with an honest message. */
  minutes: z.object({ used: z.number(), quota: z.number(), exhausted: z.boolean() }).strict(),
  degradeBanner: z.string().nullable(),
  transcript: z.array(
    z.object({ role: z.enum(['user', 'assistant']), text: z.string(), at: IsoDateTimeSchema }).strict(),
  ),
  /** Tools registered for this session from the caller's entitlements — read-only + journal write only. */
  tools: z.array(z.string()),
});
export type VoiceSessionState = z.infer<typeof VoiceSessionStateSchema>;

export const ComposerAnswerSchema = withMeta({
  queryId: IdSchema,
  skill: VoiceSkillSchema,
  answer: z.string(),
  /** Evidence references by construction (numbers trace to aggregates). */
  evidence: z.array(z.object({ kind: z.string(), ref: z.string() }).strict()),
  /** Optional chart/widget push to screen. */
  push: z
    .object({ screen: z.string(), params: z.record(z.string(), z.string()) })
    .strict()
    .nullable(),
  /** Narrow workflow intents need spoken/typed confirmation naming the object. */
  confirmation: z.object({ intent: z.string(), object: z.string() }).strict().nullable(),
  latencyMs: z.number().int(),
});

export const voiceProcedures = defineProcedures({
  'voice.session.ticket': mutation(
    'Issue a 60s device-bound voice ticket',
    z.object({ deviceId: IdSchema }).strict(),
    VoiceTicketSchema,
  ),
  'voice.session.state': query(
    'Authoritative voice session state (client store mirrors it)',
    z.void(),
    VoiceSessionStateSchema,
  ),
  'voice.session.end': mutation(
    'End the voice session (transcript persists for journaling/audit)',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    VoiceSessionStateSchema,
  ),
  'voice.composer.ask': mutation(
    'Ask PointFive (⌘K text mode — same orchestrator, skills, tools, guardrails)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        text: z.string().min(1).max(2000),
        confirm: z.boolean().optional(),
      })
      .strict(),
    ComposerAnswerSchema,
  ),
  'voice.composer.history': query(
    'Composer conversation history grouped by day',
    z.void(),
    withMeta({ groups: z.array(ConvGroupSchema) }),
  ),
});
export type VoiceFamilyClient = RouterShape<typeof voiceProcedures>;

export const voiceFamily = defineFamily({
  name: 'voice',
  title: 'Voice (kernel)',
  screens: ['Voice surfaces'],
  procedures: voiceProcedures,
  binding: 'simulated',
});
